import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_SCORING_CATEGORIES_QUERY } from './queries';
import { SCORING_CATEGORY_DELETED } from './subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_SCORING_CATEGORY } from './mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import ScoringCategoryItem from './ScoringCategoryItem';
import useSubscription from 'hooks/apollo/useSubscription';

export const scoringCategoriesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'ScoringCategories.ScoringName',
      type: 'text'
    },
    {
      name: 'code',
      caption: 'ScoringCategories.ScoringCode',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'ScoringCategories.ScoringName',
      value: 'ScoringName'
    },
    {
      caption: 'ScoringCategories.ScoringCode',
      value: 'ScoringCode'
    }
  ],
  pager: {
    orderBy: 'ScoringName',
    direction: 'asc'
  }
});

const ScoringCategoryListBrowser = () => {
  const [t] = useTranslation();
  const confirm = useConfirmDialog();
  const addToast = useToast();
  const [activeService] = useContext(ServiceContext);
  const client = useApolloClient();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_SCORING_CATEGORIES_QUERY,
        name: 'pagedScoringCategories'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(SCORING_CATEGORY_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('ScoringCategories.ScoringCategoryDeleted'), 'success');
      client.cache.evict({ id: `ScoringCategoryItem:{"scoringId":${data.scoringCategoryDeleted.scoringId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedScoringCategories' });
      client.cache.gc();
    }
  });

  const [deleteScoringCategory] = useMutation(DELETE_SCORING_CATEGORY);

  const handleDelete = useCallback(
    scoringId => {
      confirm(
        () => {
          deleteScoringCategory({ variables: { input: { scoringId: scoringId } } });
        },
        {
          content: t('ScoringCategories.Delete')
        }
      );
    },
    [deleteScoringCategory, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedScoringCategories' });
      client.cache.gc();
    }
  }, [activeService, client]);

  const itemComponent = useCallback(({ data }) => <ScoringCategoryItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'orderNo', caption: 'ScoringCategories.OrderNo', width: '100px' },
      { name: 'scoringCode', caption: 'ScoringCategories.ScoringCode' },
      { name: 'scoringName', caption: 'ScoringCategories.ScoringName' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='scoring-categories-browser' store={scoringCategoriesStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={scoringCategoriesStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={scoringCategoriesStore} options={fetchingOptions} />
    </Card>
  );
};

export default ScoringCategoryListBrowser;
